import React, { useEffect, useMemo } from 'react'

import {
  BackdropLoader,
  Box,
  Card,
  DateRangePresetOption,
  FormLabel,
  LinearProgress,
  PaginatedTable,
  SimpleTableProps,
  Typography,
  makeAppStyles,
  FilterPanel,
  Grid,
  RadioFormGroup,
  CheckboxGroup,
  ProviderLogo,
  formatMoney,
  DateRangePresetInput,
  resolveDateRangePresetLabel,
  ReportRange,
  MenuOption,
  RoundedPlainTextButtonMenu,
  Divider,
  Chip,
  RoundedPlainTextButton,
  CircularProgress,
  Dialog,
  Loader,
  BoxProps,
} from '@percept/mui'

import { ArrowDropDown, CloudDownload, ErrorOutline } from '@percept/mui/icons'

import {
  stringifyUrlSearchParams,
  useDocumentTitle,
  useUrlState,
} from '@percept/hooks'

import {
  DataWarehouseProvider,
  DataWarehouseQueryHookProps,
  useDataWarehouseAvailability,
  useDataWarehouseExport,
  useDataWarehouseQuery,
  useDataWarehouseQueryGroupings,
} from './hooks'


import { useActivePlatformUnit } from 'components/Organisation'

import { currencyOptions } from 'components/CurrencySelect'

import { DataWarehouseOrgUnitSelect, GenericTableRow, getDataWarehouseTableProps, isNewLayout } from './lib'

import { get, intersection, some } from 'lodash-es'

import { deslugify, isPlatformUnitContainer, produceKeyedMapping } from '@percept/utils'

import { useSavedQueryStrings, useTargetCurrency } from 'hooks'

import {
  allProvidersOverlap,
  dateRangeValueToDateRangeUrlState,
  FilterPanelDateRange,
  getAvailableDateRange,
  getAvailableReportGroupings,
  getLatestProviderOverlap,
  getOrgProviderAvailability,
  getOrgProviderAvailabilityByGrouping,
  getSupportedProviders,
  isProviderCompatible,
  resolveDateRangeValue,
  resolveProviderAvailability,
  useOrgUnitHierarchy,
} from './utils'

import { providerLabelMap } from '@percept/constants'


const useStyles = makeAppStyles( theme => ({
  filterCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    minHeight: 'min-content',
  },
  dialogPaper: {
    alignSelf: 'flex-start',
  },
  radioFormLabelTopAligned: {
    alignItems: 'start',
  },
  filterSelector: {
    display: 'flex',
    flexDirection: 'column',
    marginRight: theme.spacing(2),
    '&:last-of-type': {
      marginRight: 0,
    },
  },
  filterLabel: {
    fontSize: 11,
    marginLeft: 10,
    marginBottom: 6,
  },
  reportTitle: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(1.5),
  },
  reportDateRange: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(1.5),
    color: theme.palette.text.secondary,
  },
  groupingTitle: {
    fontSize: 14,
    marginTop: theme.spacing(1),
  },
  groupingExplainer: {
    marginTop: 2,
    fontSize: 12,
    lineHeight: 1.5,
    maxWidth: '14rem',
    whiteSpace: 'normal',
    marginBottom: theme.spacing(1.5),
  },
  reportTypeFormLabel: {
    minWidth: 'max-content',
    marginRight: theme.spacing(1),
  },
  reportTypeButton: {
    minWidth: 'max-content',
  },
  reportTypeInfo: {
    marginTop: theme.spacing(0.75),
    marginLeft: theme.spacing(3),
    flexShrink: 1,
    fontSize: 16,
    color: theme.palette.text.secondary,
  },
  reportTypeLabelTitle: {
    fontSize: 16,
    fontWeight: theme.typography.fontWeightBold,
  },
  reportTypeProviderChip: {
    marginLeft: theme.spacing(1),
    '&:first-of-type': {
      marginLeft: theme.spacing(2),
    },
  },
  reportTypeProviderChipRoot: {
    height: 20,
  },
  reportTypeProviderChipLabel: {
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
  },
  reportTypeLabelInfo: {
    fontSize: 12,
    maxWidth: '35rem',
    whiteSpace: 'normal',
    lineHeight: 1.4,
    paddingTop: 2,
    paddingBottom: 6,
  },
  formGroup: {
    backgroundColor: theme.palette.background.paper,
  },
  formControlLabelTopAligned: {
    alignItems: 'start',
  },
  formControlLabelPrimaryText: {
    fontSize: 14,
    marginTop: theme.spacing(1),
  },
  formControlLabelSecondaryText: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 2,
    fontSize: 13,
    lineHeight: 1.5,
    maxWidth: '21rem',
    whiteSpace: 'normal',
    marginBottom: theme.spacing(1.5),
  },
  tableCard: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  tablePaginationRoot: {
    overflow: 'unset',
  },
  refetchIndicator: {
    height: 4,
    width: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: theme.zIndex.appBar + 4,
  },
  loaderContent: {
    marginTop: theme.spacing(1.5),
  },
}))

export type DataWarehouseState = {
  reportType?: string
  reportGrouping?: string
  organisations: string[]
  providers: DataWarehouseProvider[]
  dateRangePreset: DateRangePresetOption
  start?: string
  end?: string
  currency: string
}

type FilterPanelState = Omit<DataWarehouseState, 'dateRangePreset' | 'start' | 'end'> & {
  dateRange: FilterPanelDateRange
}

const urlStateToFilterPanelState = ({
  state,
  minDate,
  maxDate,
}: {
  state: DataWarehouseState,
  minDate: Date | null,
  maxDate: Date | null,
}): FilterPanelState => ({
  ...state,
  dateRange: resolveDateRangeValue({
    dateRangePreset: state.dateRangePreset,
    start: state.start,
    end: state.end,
    minDate,
    maxDate,
  })
})


const dateRangePresetOptions: DateRangePresetOption[] = (
  process.env.WORKLOAD_CONTEXT === 'VODAFONE' ? [
    'current-financial-year',
    'current-year',
    'last-financial-year',
    'last-year',
    'last-quarter',
    'last-month',
    'last-week',
    'last-30-days',
    'last-7-days',
    'custom',
  ] : [
    'current-year',
    'last-year',
    'last-quarter',
    'last-month',
    'last-week',
    'last-30-days',
    'last-7-days',
    'custom',
  ]
)

// Consistent loader key to avoid re-render affecting animation
const LOADER_KEY = 'dwh-loader'


export const DataWarehouse = () => {

  const activePlatformUnit = useActivePlatformUnit()

  useDocumentTitle({
    paths: [
      activePlatformUnit && activePlatformUnit.name,
      'Reporting',
    ]
  })

  const orgUnitId = activePlatformUnit && activePlatformUnit.id

  const orgUnitHierarchy = useOrgUnitHierarchy({ orgUnitId })

  const availabilityQueryResult = useDataWarehouseAvailability({
    org_unit_id: orgUnitId,
  })

  const orgAvailabilityByGrouping = useMemo(() => {
    if( availabilityQueryResult.data && orgUnitHierarchy ){
      return getOrgProviderAvailabilityByGrouping(
        availabilityQueryResult.data,
        orgUnitHierarchy
      )
    }
    return null
  }, [availabilityQueryResult.data, orgUnitHierarchy])

  const groupingsQueryResult = useDataWarehouseQueryGroupings()

  const reportGroupings = useMemo(() => {
    return getAvailableReportGroupings({
      availability: availabilityQueryResult.data,
      reportGroupings: groupingsQueryResult.data,
    })
  }, [availabilityQueryResult.data, groupingsQueryResult.data])

  const defaultOrganisations = orgUnitId ? [orgUnitId] : []

  const [targetCurrency] = useTargetCurrency()

  const [state, setState] = useUrlState<DataWarehouseState>({
    reportType: undefined,
    reportGrouping: undefined,
    organisations: defaultOrganisations,
    providers: [],
    dateRangePreset: dateRangePresetOptions[0],
    start: undefined,
    end: undefined,
    currency: targetCurrency,
  })

  const [, setSavedQueryStrings] = useSavedQueryStrings()

  useEffect(() => {
    if( orgUnitId ){
      setSavedQueryStrings({
        [`dataWarehouse|${orgUnitId}`]: stringifyUrlSearchParams(state)
      })
    }
  }, [state, orgUnitId, setSavedQueryStrings])

  const groupingsByKey = produceKeyedMapping(reportGroupings, 'key')

  const grouping = get(groupingsByKey, state.reportType || '', null)
  const availableSubGroupings = get(grouping, 'sub_groupings', [])
  const sub_grouping = availableSubGroupings.find( g => g.key === state.reportGrouping) || null

  const orgProviderAvailability = getOrgProviderAvailability({
    orgAvailabilityByGrouping,
    reportGrouping: grouping,
  })

  const [minDate, maxDate] = getAvailableDateRange({
    providerAvailability: resolveProviderAvailability({
      orgUnitIds: state.organisations,
      orgProviderAvailability,
    }),
    providers: state.providers,
  })

  const dateRangeValue = resolveDateRangeValue({
    dateRangePreset: state.dateRangePreset,
    start: state.start,
    end: state.end,
    minDate,
    maxDate,
  })

  const availableOrgUnits = (
    !activePlatformUnit ?
      [] :
      [activePlatformUnit, ...(activePlatformUnit.children || [])]
  )

  const orgUnitMapping = produceKeyedMapping(availableOrgUnits, 'id')

  useEffect(() => {
    if( !state.organisations.length && activePlatformUnit ){
      setState({ organisations: [activePlatformUnit.id] })
    }
  }, [activePlatformUnit, state.organisations, setState])

  useEffect(() => {
    if(
      !state.reportType
      && reportGroupings.length
      && orgAvailabilityByGrouping
    ){
      const defaultGrouping = reportGroupings[0]
      const reportType = defaultGrouping.key

      const orgProviderAvailability = getOrgProviderAvailability({
        orgAvailabilityByGrouping,
        reportGrouping: defaultGrouping,
      })

      const providerAvailability = resolveProviderAvailability({
        orgUnitIds: state.organisations,
        orgProviderAvailability,
      })

      const overlappingProviders = getLatestProviderOverlap(providerAvailability)
      const availableProviders = overlappingProviders

      const matchedProvidersToState = intersection(state.providers, availableProviders)

      const providers = (
        matchedProvidersToState.length ?
          matchedProvidersToState :
          availableProviders
      )

      const [minDate, maxDate] = getAvailableDateRange({
        providerAvailability: resolveProviderAvailability({
          orgUnitIds: state.organisations,
          orgProviderAvailability,
        }),
        providers,
      })

      const dateRangeValue = resolveDateRangeValue({
        dateRangePreset: state.dateRangePreset,
        minDate,
        maxDate,
        start: state.start,
        end: state.end,
      })

      const update: Partial<DataWarehouseState> = {
        reportType,
        providers,
        ...dateRangeValueToDateRangeUrlState(dateRangeValue),
      }
      if( defaultGrouping.sub_groupings && defaultGrouping.sub_groupings.length ){
        update.reportGrouping = defaultGrouping.sub_groupings[0].key
      }
      setState(update)
    }
  }, [
    state.reportType,
    state.providers,
    state.dateRangePreset,
    state.start,
    state.end,
    state.organisations,
    reportGroupings,
    orgAvailabilityByGrouping,
    setState
  ])

  const queryHookProps: DataWarehouseQueryHookProps = {
    grouping: grouping && grouping.key,
    sub_grouping: sub_grouping && sub_grouping.key,
    org_unit_ids: state.organisations,
    providers: state.providers,
    start: dateRangeValue.value[0] && new Date(dateRangeValue.value[0]) || null,
    end: dateRangeValue.value[1] && new Date(dateRangeValue.value[1]) || null,
    currency: state.currency,
    base_segmentations: ['ORG_UNIT', 'PROVIDER'],
    aggregate_to_single_org_unit: !!(
      activePlatformUnit
      && state.organisations.length === 1
      && activePlatformUnit.children
      && activePlatformUnit.children.length
      && state.organisations[0] === activePlatformUnit.id
    ),
  }

  const queryResult = useDataWarehouseQuery(queryHookProps)

  const exportQueryResult = useDataWarehouseExport()

  const tableProps: SimpleTableProps<GenericTableRow> = useMemo(() => {
    if( !queryResult.data ){
      return {
        grouped: false,
        columns: [],
        rows: [],
      } as unknown as SimpleTableProps<GenericTableRow>
    }
    return getDataWarehouseTableProps(queryResult.data, state.currency) as SimpleTableProps<GenericTableRow>
  }, [queryResult.data, state.currency])

  const classes = useStyles()

  if( !activePlatformUnit ){
    return (
      <BackdropLoader key={LOADER_KEY} />
    )
  }

  if( !availabilityQueryResult.data || !groupingsQueryResult.data ){
    return (
      <BackdropLoader key={LOADER_KEY}>
        <Typography variant='h3' className={classes.loaderContent}>
          Loading report availability...
        </Typography>
      </BackdropLoader>
    )
  }

  const orgUnitType = deslugify(orgUnitHierarchy && orgUnitHierarchy.unit_type || 'ORGANISATION')

  const boxProps: BoxProps = (
    isNewLayout ?
      { pt: 2, maxHeight: 'calc(100vh - 76px)' } :
      { pt: 5, maxHeight: 'calc(100vh - 100px)' }
  )

  return (
    <Box
      display='flex'
      flexDirection='column'
      px={3}
      pb={1}
      {...boxProps}>

      <Card className={classes.filterCard}>
        <FilterPanel
          DialogProps={{
            classes: {
              paper: classes.dialogPaper,
            },
          }}
          values={urlStateToFilterPanelState({ state, minDate, maxDate })}
          onConfirm={(values): void => {
            setState({
              ...values,
              ...dateRangeValueToDateRangeUrlState(values.dateRange),
            })
          }}
          valueOrder={
            [
              'reportType',
              'reportGrouping',
              'dateRange',
              'organisations',
              'providers',
              'currency',
            ]
          }
          displayConfig={{
            /* eslint-disable react/display-name, react/prop-types */
            reportType: {
              label: 'Report Type',
              nullable: false,
              ChipProps: {
                color: 'primary',
              },
              render: (value) => get(groupingsByKey, [value, 'title'], 'Loading...'),
            },
            reportGrouping: {
              label: 'Behaviour',
              nullable: false,
              hidden: !availableSubGroupings.length,
              render: (value) => {
                const match = availableSubGroupings.find( g => g.key === value )
                if( match ){
                  return match.title
                }
                return null
              }
            },
            dateRange: {
              label: 'Date Range',
              nullable: false,
              render: ({ preset, value }) => resolveDateRangePresetLabel(preset, value),
            },
            organisations: {
              label: 'Organisations',
              nullable: false,
              minLength: 1,
              render: (value) => get(orgUnitMapping, [value, 'name'], null),
            },
            providers: {
              label: 'Providers',
              nullable: false,
              minLength: 1,
              render: (value) => (
                isNewLayout ?
                  providerLabelMap[value] :
                  <ProviderLogo size={1.25} provider={value} style={{position: 'relative', top: 4}} />
              ),
            },
            currency: {
              label: 'Currency',
              nullable: false,
              render: (value) => (
                <span>
                  {formatMoney({ amount: null, currency: value })} {value}
                </span>
              )
            },
            /* eslint-enable react/display-name, react/prop-types */
          }}>
          { ({ values, updateValues }): JSX.Element => {
            const activeGrouping = (values.reportType && groupingsByKey[values.reportType]) || null

            const orgProviderAvailability = getOrgProviderAvailability({
              orgAvailabilityByGrouping,
              reportGrouping: activeGrouping,
            })

            const providerAvailability = resolveProviderAvailability({
              orgUnitIds: values.organisations,
              orgProviderAvailability,
            })

            const [minDate, maxDate] = getAvailableDateRange({
              providerAvailability,
              providers: values.providers,
            })

            const supportedProviders = getSupportedProviders({
              rootOrgUnitId: orgUnitId,
              reportGrouping: activeGrouping,
              orgProviderAvailability,
            })

            const providerOptions: MenuOption<DataWarehouseProvider>[] = (
              supportedProviders.map( provider => {
                // Some provider date ranges may be completely incompatible with the selected
                // date range or other providers. If this is the case we need to disable the
                // option.
                const matchedAvailability = providerAvailability.find( a => a.provider === provider )
                const isCompatible = !!matchedAvailability && isProviderCompatible({
                  provider,
                  selectedProviders: values.providers,
                  providerAvailability,
                })
                return {
                  value: provider,
                  disabled: !isCompatible,
                  label: (
                    <div>
                      <ProviderLogo
                        className={classes.formControlLabelPrimaryText}
                        size={1.35} provider={provider} style={{position: 'relative', top: 3}} />
                      { matchedAvailability ? (
                        <ReportRange
                          TypographyProps={{className: classes.formControlLabelSecondaryText}}
                          start={matchedAvailability.start}
                          end={matchedAvailability.end} />
                      ) : (
                        <Typography
                          className={classes.formControlLabelSecondaryText}>
                          { values.organisations.length ? 'No data available' : `No ${orgUnitType.toLowerCase()}s selected` }
                        </Typography>
                      )}
                    </div>
                  ),
                }
              })
            )

            const hasUnavailableProviders = some(providerOptions, o => o.disabled)

            return (
              <Grid container spacing={5}>
                <Grid item xs={12}>
                  <Box display='flex' alignItems='flex-start'>
                    <Box display='flex' alignItems='center'>
                      <FormLabel className={classes.reportTypeFormLabel}>Report Type</FormLabel>
                      <RoundedPlainTextButtonMenu
                        TriggerProps={{
                          className: classes.reportTypeButton,
                          variant: 'contained',
                          color: 'primary',
                          endIcon: <ArrowDropDown />
                        }}
                        label={activeGrouping && activeGrouping.title}
                        value={values.reportType}
                        options={
                          reportGroupings.map( grouping => {
                            const orgProviderAvailability = getOrgProviderAvailability({
                              orgAvailabilityByGrouping,
                              reportGrouping: grouping,
                            })
                            const possibleProviders = getSupportedProviders({
                              rootOrgUnitId: orgUnitId,
                              reportGrouping: grouping,
                              orgProviderAvailability,
                            })
                            return {
                              value: grouping.key,
                              label: (
                                <div>
                                  <Box display='flex' alignItems='center' pt={1}>
                                    <Typography className={classes.reportTypeLabelTitle}>
                                      { grouping.title }
                                    </Typography>
                                    { !!possibleProviders.length && (
                                      <Box display='flex' alignItems='center'>
                                        { possibleProviders.map( provider => (
                                          <Chip
                                            key={provider}
                                            className={classes.reportTypeProviderChip}
                                            color='default'
                                            variant='outlined'
                                            classes={{
                                              root: classes.reportTypeProviderChipRoot,
                                              label: classes.reportTypeProviderChipLabel,
                                            }}
                                            size='small'
                                            label={
                                              <ProviderLogo
                                                provider={provider}
                                                style={{position: 'relative', top: 4}}
                                                size={15}
                                                units='px' />
                                            } />
                                        ))}
                                      </Box>
                                    )}
                                  </Box>
                                  { !!grouping.info && (
                                    <Typography className={classes.reportTypeLabelInfo}>
                                      { grouping.info }
                                    </Typography>
                                  )}
                                </div>
                              ),
                            }
                          })
                        }
                        onChange={(e, reportType): void => {
                          const matchedGrouping = reportGroupings.find( g => g.key === reportType )
                          const orgProviderAvailability = getOrgProviderAvailability({
                            orgAvailabilityByGrouping,
                            reportGrouping: matchedGrouping || null,
                          })

                          const providerAvailability = resolveProviderAvailability({
                            orgUnitIds: values.organisations,
                            orgProviderAvailability,
                          })

                          const overlappingProviders = getLatestProviderOverlap(providerAvailability)
                          const matchedProvidersToState = intersection(values.providers, overlappingProviders)
                          const providers = (
                            matchedProvidersToState.length ?
                              matchedProvidersToState :
                              overlappingProviders
                          )

                          const [minDate, maxDate] = getAvailableDateRange({
                            providerAvailability,
                            providers,
                          })

                          const organisations = intersection(
                            state.organisations,
                            Object.keys(orgProviderAvailability),
                          )

                          updateValues({
                            reportType,
                            providers,
                            organisations,
                            dateRange: resolveDateRangeValue({
                              dateRangePreset: values.dateRange.preset,
                              start: values.dateRange.value[0],
                              end: values.dateRange.value[1],
                              minDate,
                              maxDate,
                            })
                          })
                        }} />
                    </Box>

                    { !!(activeGrouping && activeGrouping.info) && (
                      <Typography className={classes.reportTypeInfo}>
                        { activeGrouping.info }
                      </Typography>
                    )}
                  </Box>
                </Grid>

                { !!(activeGrouping && activeGrouping.sub_groupings && activeGrouping.sub_groupings.length) && (
                  <Grid item xs='auto'>
                    <RadioFormGroup
                      name='Report Behaviour'
                      value={values.reportGrouping}
                      FormControlLabelProps={{
                        className: classes.radioFormLabelTopAligned,
                      }}
                      options={
                        (activeGrouping.sub_groupings || []).map( sub_grouping => ({
                          value: sub_grouping.key,
                          label: (
                            <div>
                              <Typography className={classes.groupingTitle}>
                                {sub_grouping.title}
                              </Typography>
                              { sub_grouping.info && (
                                <Typography className={classes.groupingExplainer}>
                                  {sub_grouping.info}
                                </Typography>
                              )}
                            </div>
                          ),
                        }))
                      }
                      onChange={(reportGrouping): void => {
                        updateValues({ reportGrouping })
                      }} />
                  </Grid>
                )}

                { orgUnitHierarchy && isPlatformUnitContainer(orgUnitHierarchy) && (
                  <Grid item xs='auto'>
                    <FormLabel>
                      { orgUnitType }s
                    </FormLabel>
                    <DataWarehouseOrgUnitSelect
                      orgUnit={orgUnitHierarchy}
                      enabledOrgs={Object.keys(orgProviderAvailability)}
                      selectedIds={values.organisations}
                      onChange={(organisations): void => {
                        organisations = organisations.filter( orgUnitId => (
                          !!orgProviderAvailability[orgUnitId]
                        ))
                        const providerAvailability = resolveProviderAvailability({
                          orgUnitIds: organisations,
                          orgProviderAvailability,
                        })
                        const providerOptions = getLatestProviderOverlap(providerAvailability)
                        const commonProviders = intersection(
                          providerOptions,
                          values.providers,
                        )
                        const providers = commonProviders.length ? commonProviders : providerOptions
                        const [minDate, maxDate] = getAvailableDateRange({
                          providerAvailability,
                          providers,
                        })
                        updateValues({
                          providers,
                          organisations,
                          dateRange: resolveDateRangeValue({
                            dateRangePreset: values.dateRange.preset,
                            start: values.dateRange.value[0],
                            end: values.dateRange.value[1],
                            minDate,
                            maxDate,
                          })
                        })
                      }}
                      CheckboxGroupProps={{
                        scrollable: true,
                        maxHeight: '32rem',
                        FormGroupProps: {
                          className: classes.formGroup,
                        }
                      }} />
                  </Grid>
                )}

                <Grid item xs='auto'>
                  <CheckboxGroup
                    name='Providers'
                    value={values.providers}
                    disableSelectAll={!allProvidersOverlap(providerAvailability)}
                    FormControlLabelProps={{
                      className: classes.formControlLabelTopAligned,
                    }}
                    options={providerOptions}
                    onChange={(providers): void => {
                      const orgProviderAvailability = getOrgProviderAvailability({
                        orgAvailabilityByGrouping,
                        reportGrouping: activeGrouping,
                      })
                      const providerAvailability = resolveProviderAvailability({
                        orgUnitIds: values.organisations,
                        orgProviderAvailability,
                      })
                      const [minDate, maxDate] = getAvailableDateRange({
                        providerAvailability,
                        providers,
                      })
                      updateValues({
                        providers,
                        dateRange: resolveDateRangeValue({
                          dateRangePreset: values.dateRange.preset,
                          start: values.dateRange.value[0],
                          end: values.dateRange.value[1],
                          minDate,
                          maxDate,
                        })
                      })
                    }} />

                  { hasUnavailableProviders && (
                    <Box
                      display='flex'
                      alignItems='flex-start'
                      flexGrow={0}
                      mt={2}>
                      <ErrorOutline
                        color='error'
                        fontSize='small'
                        style={{
                          position: 'relative',
                          top: 2,
                          marginRight: 4,
                        }} />
                      <div>
                        <Typography style={{maxWidth: 130}}>
                          { values.organisations.length ? (
                            'Some providers cannot be included as data is '
                            + 'unavailable for the selected date range'
                          ) : (
                            `Select one or more ${orgUnitType.toLowerCase()}s to view provider availability`
                          )}
                        </Typography>
                      </div>
                    </Box>
                  )}

                </Grid>

                <Grid item xs='auto'>
                  <FormLabel>Date Range</FormLabel>
                  <DateRangePresetInput
                    BoxProps={{
                      mt: 1,
                    }}
                    variant='static'
                    divided
                    color='secondary'
                    dateRangePreset={values.dateRange.preset}
                    dateRangePresetOptions={dateRangePresetOptions}
                    value={values.dateRange.value}
                    minDate={minDate}
                    maxDate={maxDate}
                    onChange={(value, preset): void => {
                      updateValues({
                        dateRange: {
                          preset,
                          value,
                        }
                      })
                    }} />
                </Grid>

                <Grid item xs='auto'>
                  <RadioFormGroup
                    name='Currency'
                    value={values.currency}
                    options={currencyOptions}
                    onChange={(currency): void => {
                      updateValues({ currency })
                    }} />
                </Grid>

              </Grid>
            )
          }}
        </FilterPanel>
      </Card>

      { queryResult.isLoading && (
        <BackdropLoader key={LOADER_KEY}>
          { grouping && (
            <Typography variant='h3' className={classes.loaderContent}>
              Loading {grouping.title} report...
            </Typography>
          )}
        </BackdropLoader>
      )}
      { exportQueryResult.isLoading && (
        <Dialog open maxWidth='sm'>
          <Box m={3}>
            <Loader justifySelf='center' alignSelf='center' mb={2} />
            { grouping && (
              <Typography variant='h4' className={classes.loaderContent}>
                Downloading {grouping.title} report...
              </Typography>
            )}
          </Box>
        </Dialog>
      )}
      { queryResult.isError && (
        <Box p={6} display='flex' flexDirection='column' alignItems='center' justifyContent='center' color='text.secondary'>
          <Box display='flex' alignItems='center'>
            <Box display='flex' mr={1}>
              <ErrorOutline color='error' fontSize='large' />
            </Box>
            <Typography variant='h4' color='textSecondary'>
              { grouping ? `${grouping.title} report` : 'Report' } could not be loaded
            </Typography>
          </Box>
          { queryResult.error.response && queryResult.error.response.data && queryResult.error.response.data.detail && (
            <Box mt={1}>
              <Typography variant='h5' color='textSecondary'>
                { queryResult.error.response.data.detail }
              </Typography>
            </Box>
          )}
        </Box>
      )}
      { queryResult.data && (
        queryResult.data.rows.length === 0 ? (
          <Box p={6} display='flex' alignItems='center' justifyContent='center'>
            <Typography variant='h4' color='textSecondary'>
              No data is available for the { grouping ? `${grouping.title} report` : 'report' } with
              the selected filters applied
            </Typography>
          </Box>
        ) : (
          <Card className={classes.tableCard}>
            { queryResult.isRefetching && (
              <LinearProgress variant='indeterminate' className={classes.refetchIndicator} />
            )}
            { grouping && (
              <>
                <Box display='flex' justifyContent='space-between'>
                  <Typography variant='h5' className={classes.reportTitle}>
                    { grouping.title } Report
                    <ReportRange
                      TypographyProps={{
                        variant: 'inherit',
                        className: classes.reportDateRange,
                      }}
                      start={queryResult.data.metadata.period.start_date}
                      end={queryResult.data.metadata.period.end_date} />
                  </Typography>

                  <Box p={1.5}>
                    <RoundedPlainTextButton
                      variant='contained'
                      size='small'
                      startIcon={
                        exportQueryResult.isLoading ?
                          <CircularProgress size='1em' color='inherit' /> :
                          <CloudDownload />
                      }
                      disabled={exportQueryResult.isLoading}
                      onClick={(): void => {
                        exportQueryResult.mutate(queryHookProps)
                      }}>
                      Download CSV
                    </RoundedPlainTextButton>
                  </Box>
                </Box>
                <Divider />
              </>
            )}
            <PaginatedTable
              paginate={100}
              size='small'
              sortable
              stickyHeader
              unsetStickyHeaderZIndex={false}
              pinFirstColumn
              wrapCellText={false}
              TablePaginationProps={{
                classes: {
                  root: classes.tablePaginationRoot,
                },
              }}
              {...tableProps} />
          </Card>
        )
      )}
    </Box>
  )
}
